import { BariatricProcedureStage, BariatricProcedureStageLabel } from '@bariendo/lib/constants';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION } from '@bariendo/lib/extensions';
import { Badge, Combobox, Group, Stack, Text, TextInput, useCombobox } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { formatHumanName, getExtension, getReferenceString } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { usePostHog } from 'posthog-js/react';
import { trackSearchPerformed } from '../services/analytics';
import { formatBirthDate } from '@/services/patient';
import { ContactPointType } from '@/components/Timeline/types';
import { HighlightableText } from '@/components/HighlightableText';
import { reportError } from '@/services/error-reporting';
import { PatientSearchBranch, buildPatientSearchQuery } from '@/services/patient-search';

type SearchResourcesResponse = {
  PatientList: Patient[];
};

type GraphQLResponse<T> = {
  data: T;
  errors?: any[];
};

interface SearchResults {
  patients: Patient[];
  /** Which axis matched, so the row can show the contact value the name doesn't explain. */
  branch: PatientSearchBranch;
  term: string;
}

const EMPTY_RESULTS: SearchResults = { patients: [], branch: PatientSearchBranch.NAME, term: '' };

/**
 * The contact value responsible for a phone or email hit. Without it the row shows only a name
 * and a date of birth, and a match on a number the reader typed looks arbitrary.
 */
function matchedContact(patient: Patient, branch: PatientSearchBranch, term: string): string | undefined {
  if (branch === PatientSearchBranch.NAME) return undefined;

  const system = branch === PatientSearchBranch.PHONE ? ContactPointType.PHONE : ContactPointType.EMAIL;
  const candidates = patient.telecom?.filter((contact) => contact.system === system && contact.value) ?? [];

  const matches =
    branch === PatientSearchBranch.PHONE
      ? candidates.find((contact) => contact.value?.replace(/\D/g, '').includes(term))
      : candidates.find((contact) => contact.value?.toLowerCase().includes(term.toLowerCase()));

  // A patient can match on a secondary contact point the server saw but this filter misses;
  // showing the first one is still more informative than showing none.
  return (matches ?? candidates[0])?.value;
}

function PatientsGroup({ patients, branch, term }: SearchResults) {
  if (!patients?.length) return null;
  return (
    <Combobox.Group label="Patients">
      {patients.map((resource) => {
        const name = formatHumanName(resource.name?.[0]);
        const dob = resource.birthDate ? formatBirthDate(resource.birthDate) : undefined;
        const stage = getExtension(resource, BARIATRIC_PROCEDURE_STAGE_EXTENSION)?.valueString as
          | BariatricProcedureStage
          | undefined;
        const procedureStage = stage ? BariatricProcedureStageLabel[stage] : undefined;
        const contact = matchedContact(resource, branch, term);

        return (
          <Combobox.Option key={resource.id} value={`/${getReferenceString(resource)}/timeline`}>
            <Group gap={4} wrap="nowrap" align="center" justify="space-between" style={{ width: '100%' }}>
              <Stack gap={0}>
                <Group gap={4} wrap="nowrap" align="center">
                  <Text size="sm" fw={500}>
                    {name}
                  </Text>
                  {dob && (
                    <Text size="sm" c="dimmed">
                      | {dob}
                    </Text>
                  )}
                </Group>
                {contact && (
                  <HighlightableText
                    data-testid="matched-contact"
                    text={contact}
                    searchTerm={term}
                    size="xs"
                    c="dimmed"
                  />
                )}
              </Stack>
              {procedureStage && (
                <Badge size="sm" color="gray" variant="light" radius="xl" mt={2}>
                  {procedureStage}
                </Badge>
              )}
            </Group>
          </Combobox.Option>
        );
      })}
    </Combobox.Group>
  );
}

export function GlobalSearch() {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const combobox = useCombobox();
  const posthog = usePostHog();

  const [inputValue, setInputValue] = useState('');
  const [value, setValue] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);

  const fetchResults = useCallback(async () => {
    const search = buildPatientSearchQuery(value);
    if (!search) {
      setResults(EMPTY_RESULTS);
      return;
    }

    try {
      const response: GraphQLResponse<SearchResourcesResponse> = await medplum.graphql(
        search.query,
        null,
        search.variables
      );

      if (response.errors) {
        throw new Error(response.errors[0].message);
      }

      const patientResults = response.data?.PatientList ?? [];
      setResults({ patients: patientResults, branch: search.branch, term: search.term });

      // Track search performed
      trackSearchPerformed(posthog, {
        search_type: 'patient',
        source: 'global header',
        query_length: value.length,
        results_count: patientResults.length,
      });
    } catch (error) {
      // The search term is a phone number or an email address, so it never leaves the browser.
      reportError(error, { component: 'GlobalSearch', tags: { branch: search.branch } });
      setResults(EMPTY_RESULTS);
    }
  }, [value, medplum, posthog]);

  useEffect(() => {
    fetchResults();
  }, [value, fetchResults]);

  useEffect(() => {
    if (results.patients.length > 0) {
      combobox.openDropdown();
    } else {
      combobox.closeDropdown();
    }
  }, [results, combobox]);

  const debouncedSetValue = useDebouncedCallback((newValue: string) => {
    setValue(newValue);
  }, 500);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.currentTarget.value;
    setInputValue(newVal);
    debouncedSetValue(newVal);
  };

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(selectedValue) => {
        // Track selected result rank
        const allResults = results.patients;
        const selectedIndex = allResults.findIndex((p) => `/${getReferenceString(p)}/timeline` === selectedValue);

        if (selectedIndex >= 0 && value) {
          trackSearchPerformed(posthog, {
            search_type: 'patient',
            source: 'global header',
            query_length: value.length,
            results_count: allResults.length,
            selected_result_rank: selectedIndex + 1, // 1-indexed
          });
        }

        combobox.closeDropdown();
        navigate(selectedValue);
        setInputValue('');
        setValue('');
      }}
    >
      <Combobox.Target>
        <TextInput
          w="100%"
          leftSection={<IconSearch size={16} />}
          placeholder="Search"
          value={inputValue}
          onChange={handleValueChange}
          onBlur={() => {
            setInputValue('');
            setValue('');
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <PatientsGroup {...results} />
      </Combobox.Dropdown>
    </Combobox>
  );
}
